import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { getArticleById, updateArticle } from "@/lib/data";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import ArticleForm from "@/components/article/ArticleForm";
import { 
  ArticleFormValues, 
  articleFormSchema, 
  defaultFormValues 
} from "@/components/article/ArticleFormSchema";
import { useArticleUpdates } from "@/hooks/useArticleUpdates";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Custom event for article updates
export const ARTICLE_UPDATED_EVENT = 'article-updated';

const EditArticleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [originalPosition, setOriginalPosition] = useState<number | null>(null);
  const [originalKeywords, setOriginalKeywords] = useState<string[]>([]);
  const [isDraft, setIsDraft] = useState(false);
  const { recordArticleUpdate } = useArticleUpdates();
  
  const form = useForm<ArticleFormValues>({
    resolver: zodResolver(articleFormSchema),
    defaultValues: defaultFormValues,
  });
  
  useEffect(() => {
    const fetchArticle = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const article = await getArticleById(id);
        
        if (!article) {
          toast.error("Article not found");
          navigate("/");
          return;
        }
        
        setOriginalPosition(article.displayPosition || null);
        
        // Store original keywords
        setOriginalKeywords([...article.keywords]);
        
        // Store draft status
        setIsDraft(article.draft || false);
        
        const keywordsString = article.keywords.join(' ');
        
        form.reset({
          title: article.title,
          description: article.description,
          author: article.author,
          keywords: keywordsString,
          imageUrl: article.imageUrl,
          sourceUrl: article.sourceUrl || "",
          summary: article.summary || "",
          more_content: article.more_content || "",
          private: article.private || false,
          draft: article.draft || false
        });
      } catch (error) {
        toast.error("Failed to load article");
        navigate("/");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchArticle();
  }, [id, navigate, form]);

  const onSubmit = async (data: ArticleFormValues) => {
    if (!id) return;
    
    setIsSubmitting(true);

    try {
      const keywordsArray = data.keywords
        ? data.keywords
            .split(/\s+/)
            .filter(keyword => keyword.trim().length > 0)
        : [];

      // Check if venue or ott keywords have changed
      const originalHasVenue = originalKeywords.includes('venue');
      const originalHasOtt = originalKeywords.includes('ott');
      const newHasVenue = keywordsArray.includes('venue');
      const newHasOtt = keywordsArray.includes('ott');
      
      const venueChanged = originalHasVenue !== newHasVenue;
      const ottChanged = originalHasOtt !== newHasOtt;
      
      console.log(`Original keywords: ${originalKeywords.join(', ')}`);
      console.log(`New keywords: ${keywordsArray.join(', ')}`);
      console.log(`Venue changed: ${venueChanged}, OTT changed: ${ottChanged}`);
      console.log(`Original position: ${originalPosition}`);

      await updateArticle(id, {
        title: data.title,
        description: data.description,
        author: data.author || "Anonymous",
        keywords: keywordsArray,
        imageUrl: data.imageUrl || "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b",
        sourceUrl: data.sourceUrl,
        summary: data.summary,
        more_content: data.more_content,
        updatedAt: new Date().toISOString(),
        private: data.private || false,
        draft: data.draft || false
      }, {
        preservePosition: !venueChanged && !ottChanged,
        originalPosition: originalPosition
      });
      
      // Record the article update in the database
      await recordArticleUpdate(id);
      
      // Invalidate article caches so back-navigation shows updated data
      queryClient.removeQueries({ queryKey: ['articles'] });
      
      toast.success("Article updated successfully!");
      navigate(`/article/${id}`);
    } catch (error) {
      toast.error("Failed to update article. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const titleValue = form.watch("title");
  const descriptionValue = form.watch("description");
  const currentDraftValue = form.watch("draft");
  const isMissingRequired = !titleValue?.trim() || !descriptionValue?.trim();
  const isButtonDisabled = isSubmitting || isMissingRequired;

  const TopSubmitButton = (
    <Button 
      type="submit" 
      disabled={isButtonDisabled} 
      className="flex-1"
    >
      {isSubmitting ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
          {currentDraftValue ? "Saving..." : "Updating..."}
        </>
      ) : (currentDraftValue ? "Save" : "Update Article")}
    </Button>
  );

  return (
    <ArticleForm
      form={form}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
      submitButtonText="Update Article"
      isDraft={isDraft}
    >
      {/* Top buttons */}
      <div className="mb-6 flex gap-2">
        {isMissingRequired ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild className="flex-1">
                <span className="flex-1">{TopSubmitButton}</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Articles require a Title and a Description</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          TopSubmitButton
        )}
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => navigate(`/article/${id}`)} 
          className="flex-1"
        >
          Cancel
        </Button>
      </div>
    </ArticleForm>
  );
};

export default EditArticleForm;
